import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import _ from 'lodash';
import { Button, Modal, FormCheck } from 'react-bootstrap';
import CustomerLookup from '../lookups/CustomerLookup';
import toastr from '../../../toastr';

import { Api, IApiResponse } from '../../typings/Api';
import { ICustomerParams } from '../../typings/CustomerParameters';
import { IShipToAddress } from '../../model/ShopKeeper.model';

import Label from '../Label';
import ZipcodeLookup from '../lookups/ZipcodeLookup';

interface IProps {
  shipToAccountID: string;
  shipToAccountDetails: ICustomerParams | null;
  shipToEmail: boolean;
  shipToAddress: IShipToAddress | null;
  clearShipToOverride: Function;
  container: Object | HTMLElement | null;
  codAccount: boolean;
  codAccountDefaults: { city: string; state: string; zipcode: string } | null;
  hide: Function;
  hideInactiveAccounts: boolean;
  loadShipToAccount: (customerID: string, details: ICustomerParams, shipToEmail: boolean) => void;
  loadShipToAddress: (details: IShipToAddress) => void;
  show: boolean;
  showAdditionalNames: boolean;
  showCODCrossReferences: Function;
  useZipcodeList: boolean;
}

const ShipToOverrideModal: FunctionComponent<IProps> = ({
  shipToAccountID,
  shipToAccountDetails,
  shipToEmail,
  shipToAddress,
  clearShipToOverride,
  container,
  codAccount,
  codAccountDefaults,
  hide,
  hideInactiveAccounts,
  loadShipToAccount,
  loadShipToAddress,
  show,
  showAdditionalNames,
  showCODCrossReferences,
  useZipcodeList,
}) => {
  const [activeShipToAccountID, setActiveShipToAccountID] = useState<string | null>(null);
  const [activeShipToDetails, setActiveShipToDetails] = useState<ICustomerParams | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastPhoneQuery, setLastPhoneQuery] = useState('');
  const [showRequiredFields, setShowRequiredFields] = useState(false);

  const customerLookupRef = useRef<CustomerLookup>(null);
  const emailShipToRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const stateInputRef = useRef<HTMLInputElement>(null);
  const zipcodeLookupRef = useRef<ZipcodeLookup>(null);
  const zipcodeLookupFallbackRef = useRef<HTMLInputElement>(null); // For those without zipcodelist
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const applyButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // timeout required for react-bootstrap portal issue
    setTimeout(() => {
      // free form ship to already entered?
      if (shipToAddress) {
        const x = shipToAddress;
        nameInputRef.current!.value = x.name;
        addressInputRef.current!.value = x.address;
        cityInputRef.current!.value = x.city;
        stateInputRef.current!.value = x.state;
        (useZipcodeList ? zipcodeLookupRef : zipcodeLookupFallbackRef).current!.value = x.zipCode;
        phoneInputRef.current!.value = x.phone;
        applyFocus();

        // ship to account already selected?
      } else if (shipToAccountID) {
        customerLookupRef.current!.value = shipToAccountID;

        setActiveShipToAccountID(shipToAccountID);
        setActiveShipToDetails(shipToAccountDetails);
        applyFocus();

        // is this a COD account load from the footer toolbar?
      } else if (codAccount) {
        // copy over the default city/state/zip for COD accounts that define them
        cityInputRef.current!.value = codAccountDefaults!.city;
        stateInputRef.current!.value = codAccountDefaults!.state;
        (useZipcodeList ? zipcodeLookupRef : zipcodeLookupFallbackRef).current!.value = codAccountDefaults!.zipcode;

        nameInputRef.current!.focus();

        // otherwise focus to ship to account control
      } else {
        customerLookupRef.current!.enableAndFocus();
      }
    }, 0);
  }, []);

  const apply = async () => {
    const name = nameInputRef.current!.value.trim();
    const address = addressInputRef.current!.value.trim();
    const city = cityInputRef.current!.value.trim();
    const state = stateInputRef.current!.value.trim();
    const zipCode = (useZipcodeList ? zipcodeLookupRef : zipcodeLookupFallbackRef).current!.value.trim();
    const phone = phoneInputRef.current!.value.trim();

    // In COD cash sale scenarios, cross refrence the customer database for a phone number match and alert accordinglyl
    if (codAccount && phone !== '') {
      const phoneQuery = phone.replace(/\D/g, ''); // We only want the digits

      // We'll only recommend the cross reference once
      if (phoneQuery !== '' && phoneQuery !== lastPhoneQuery) {
        let res;
        try {
          res = await Api.get('/list/PCU2', {
            params: {
              limit: 100,
              q: phoneQuery,
              skipAttachments: true,
            },
          });

          if (res.status === 200) {
            // Skip accounts with disabled invoicing or set to inactive status.
            let suggestions = _.get(res, 'data.list', []).filter(
              (o: any) =>
                o.Invoicing !== 'Disabled' && o.Status !== 'Inactive' && o.Phone && o.Phone.includes(phoneQuery)
            );

            setLastPhoneQuery(phoneQuery);

            if (suggestions.length > 0) {
              showCODCrossReferences(suggestions);
            }

            setIsProcessing(false);
            return;
          } else {
            throw new Error('200 not received from /list/PCU2 for cash customer cross reference lookup.');
          }
        } catch (err: any) {
          console.error(err.message);
        }
      }
    }

    // In COD cash sale scenarios, name/address/city/state/zip are required fields
    if (codAccount && !(!!name && !!address && !!city && !!state && !!zipCode)) {
      toastr.error('Error', 'Required Fields Empty');
      setShowRequiredFields(true);
      setIsProcessing(false);

      // Smart focus in order of missing information
      setTimeout(() => {
        if (!!!name) nameInputRef.current!.focus();
        else if (!!!address) addressInputRef.current!.focus();
        else if (!!!city) cityInputRef.current!.focus();
        else if (!!!state) stateInputRef.current!.focus();
        else if (!!!zipCode) (useZipcodeList ? zipcodeLookupRef : zipcodeLookupFallbackRef).current!.focus();
      }, 50);

      return;
    }

    // Give us something to work with here (dominates ship to account) ...
    if (!!name || !!address || !!city || !!state || !!zipCode || !!phone) {
      // set address
      loadShipToAddress({
        address,
        city,
        name,
        phone,
        state,
        zipCode,
      });
    } else if (activeShipToAccountID) {
      loadShipToAccount(activeShipToAccountID, activeShipToDetails as ICustomerParams, emailShipToRef.current!.checked);
    }
  };

  const applyFocus = () => {
    (ReactDOM.findDOMNode(applyButtonRef.current!) as HTMLButtonElement).focus();
  };

  // We'll hit the API to pull the customer here so that, in case of error, the modal stays displayed.
  const customerLoadHandler = async (customerID: string) => {
    let res: IApiResponse;

    try {
      res = await Api.post('/shopkeeper/customerparams', { id: customerID });
      if (res.status !== 200) {
        throw new Error('200 not received from customerparams');
      }
    } catch (err: any) {
      console.error(err.message);
      toastr.error('Error', `Unable to load ship-to account: ${customerID}`);

      setActiveShipToAccountID(null);
      setActiveShipToDetails(null);
      return;
    }

    setActiveShipToAccountID(customerID);
    setActiveShipToDetails(res.data.params);
    applyFocus();
  };

  const displayShipToAccountDetails = () => {
    // This should always be seeded.
    if (!activeShipToDetails) {
      return '';
    }

    let shipToDetails = '';
    const customerParams = activeShipToDetails as ICustomerParams;

    shipToDetails += !!customerParams.name1.trim() ? `${customerParams.name1}<br />` : '';
    shipToDetails += !!customerParams.name2.trim() ? `${customerParams.name2}<br />` : '';
    shipToDetails += !!customerParams.name3.trim() ? `${customerParams.name3}<br />` : '';
    shipToDetails += !!customerParams.address.trim() ? `${customerParams.address}<br />` : '';
    shipToDetails += `${customerParams.city} ${customerParams.state} ${customerParams.zipcode}`;
    shipToDetails += !!customerParams.phone.trim() ? `<br />${customerParams.phone}` : '';
    shipToDetails += !!customerParams.email.trim() ? `<br />${customerParams.email}` : '';

    // 2 spaces only
    if (shipToDetails === '  ') {
      shipToDetails = 'Not Specified';
    }

    return shipToDetails;
  };

  const showRequiredField = (ref: React.RefObject<HTMLInputElement | ZipcodeLookup>) =>
    showRequiredFields && ref.current!.value.trim() === '' ? 'sk-labelRequired' : undefined;

  return (
    <Modal backdrop="static" container={container as any} show={show} onHide={hide as any}>
      <Modal.Header closeButton={true}>
        <Modal.Title>{codAccount ? 'Cash Sale Information' : 'Ship To Override'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="sk-shipToOverrideModal">
          {!codAccount && (
            <div className="sk-shipToLookup">
              <fieldset>
                <legend>Ship-To Account</legend>
                <Label>Account Lookup</Label>
                <CustomerLookup
                  activeId={shipToAccountID}
                  disabled={false}
                  hideInactiveAccounts={hideInactiveAccounts}
                  id="shipToCustomerLookup"
                  onCustomerLoad={customerLoadHandler}
                  onEnterKeyPressed={() => applyFocus()}
                  onPickListLoad={() => {
                    console.error('Legacy pick list loads not supported in ship to modal.');
                  }}
                  placeholder=""
                  showAdditionalNames={showAdditionalNames}
                  style={null}
                  ref={customerLookupRef}
                />
              </fieldset>
              <div className="sk-emailShipToAccount">
                <FormCheck inline={true}>
                  <FormCheck.Input
                    type="checkbox"
                    id="email-ship-to-accont"
                    className="sk-useMaterialInput mt-3"
                    defaultChecked={shipToEmail}
                    ref={emailShipToRef}
                  />
                  <FormCheck.Label htmlFor="email-ship-to-accont">Email ship to account?</FormCheck.Label>
                </FormCheck>
              </div>
              {activeShipToAccountID && (
                <div
                  className="sk-shipToAccountOverrideDetails"
                  dangerouslySetInnerHTML={{ __html: displayShipToAccountDetails() }}
                />
              )}
            </div>
          )}

          <div className={`${codAccount ? 'sk-freeFormAddressEntryCOD' : 'sk-freeFormAddressEntry'}`}>
            <fieldset>
              <legend>Custom Ship-To Address</legend>
              <Label className={showRequiredField(nameInputRef)}>Name</Label>
              <input
                type="text"
                ref={nameInputRef}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    addressInputRef.current!.focus();
                  }
                }}
              />
              <Label className={showRequiredField(addressInputRef)}>Address</Label>
              <input
                type="text"
                ref={addressInputRef}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    cityInputRef.current!.focus();
                  }
                }}
              />
              <Label className={showRequiredField(cityInputRef)}>City</Label>
              <input
                type="text"
                ref={cityInputRef}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    stateInputRef.current!.focus();
                  }
                }}
              />
              <Label className={showRequiredField(stateInputRef)}>State</Label>
              <input
                type="text"
                ref={stateInputRef}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    (useZipcodeList ? zipcodeLookupRef : zipcodeLookupFallbackRef).current!.focus();
                  }
                }}
              />
              <Label className={showRequiredField(useZipcodeList ? zipcodeLookupRef : zipcodeLookupFallbackRef)}>
                Postal Code
              </Label>
              {useZipcodeList ? (
                <ZipcodeLookup
                  ref={zipcodeLookupRef}
                  onEnterKeyPressed={() => phoneInputRef.current!.focus()}
                  onSelected={() => phoneInputRef.current!.focus()}
                />
              ) : (
                <input
                  type="text"
                  ref={zipcodeLookupFallbackRef}
                  onKeyPress={evt => {
                    if (evt.key === 'Enter') {
                      phoneInputRef.current!.focus();
                    }
                  }}
                />
              )}
              <Label>Phone</Label>
              <input
                type="text"
                ref={phoneInputRef}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    applyFocus();
                  }
                }}
              />
            </fieldset>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ marginRight: '5px' }}>
            {(shipToAccountID !== '' || shipToAddress) && (
              <Button variant="warning" onClick={() => clearShipToOverride()}>
                Clear Override
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={() => hide()}>
              Cancel
            </Button>
            <Button
              variant="success"
              disabled={isProcessing}
              onClick={() => {
                setIsProcessing(true);
                apply();
              }}
              ref={applyButtonRef}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ShipToOverrideModal;
